<script lang="ts" setup>
import type { ShortType } from '~/types/shorts';

const visible = defineModel<boolean>('visible');
const emit = defineEmits<{
  (event: 'select', value: ShortType): void;
}>();

const { data: categories } = await useAsyncData(() => ShortAPI.categories());

assertNotEmpty(categories, () => createError({ statusCode: 500 }));

const selectCategoryDialogVisible = ref(false);
const includedCategoryIds = ref<number[]>(categories.value[0] ? [categories.value[0].id] : []);
const excludedCategoryIds = ref<number[]>([]);
const loading = ref(false);
const shortsContainer = ref<HTMLDivElement>();

const shorts = ref<ShortType[]>([]);

const includedCategories = computed(() =>
  includedCategoryIds.value.map((id) => categories.value?.find((c) => c.id === id)).filter((el) => !!el),
);

const loadShorts = (clear?: boolean) => {
  if (loading.value) return;
  loading.value = true;

  ShortAPI.random({
    query: {
      limit: 10,
      included_categories: includedCategoryIds.value,
      excluded_categories: excludedCategoryIds.value,
    },
  })
    .then((data) => {
      shorts.value = clear ? data : [...shorts.value, ...data];
    })
    .finally(() => {
      loading.value = false;
    });
};

const onScrollShorts = () => {
  if (
    shortsContainer.value &&
    shortsContainer.value.scrollTop + shortsContainer.value.clientHeight > shortsContainer.value.scrollHeight - 300
  ) {
    loadShorts();
  }
};

const selectShort = (value: ShortType) => {
  emit('select', value);
  visible.value = false;
};

watch(
  shortsContainer,
  (v) => {
    if (v) v.addEventListener('scroll', onScrollShorts);
  },
  { immediate: true },
);

watch([includedCategoryIds, excludedCategoryIds], () => {
  loading.value = false;
  loadShorts(true);
});

watch(visible, () => {
  if (!shorts.value.length) loadShorts();
});

onBeforeUnmount(() => {
  shortsContainer.value?.removeEventListener('scroll', onScrollShorts);
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    dismissable-mask
    :draggable="false"
    class="max-w-200 w-full min-h-200"
    :pt="{ content: { ref: (el: HTMLDivElement) => (shortsContainer = el) } }"
  >
    <template #header>
      <div class="flex gap-3 items-center">
        <Button @click="selectCategoryDialogVisible = true" icon="pi pi-filter" size="small" outlined rounded />
        <span class="p-dialog-title">Выбор медиа</span>
      </div>
    </template>

    <div class="flex flex-col gap-4">
      <div class="sticky top-0 py-2 flex flex-wrap gap-3">
        <Tag v-for="category in includedCategories" :key="category.id" :value="category.label" />
      </div>

      <button
        v-for="short in shorts"
        :key="short.id"
        @click="selectShort(short)"
        class="block w-full p-0 outline-none border-none bg-none"
        data-test-id="select-short-dialog-option"
      >
        <img :src="short.url" class="block w-full" />
      </button>

      <div v-if="loading" class="p-8 flex justify-center items-center">
        <i class="pi pi-spin pi-spinner text-2xl" />
      </div>

      <div v-else-if="!shorts.length" class="p-8 flex justify-center items-center text-xl">Ничего не найдено</div>
    </div>
  </Dialog>

  <CommonShortsSelectCategoryDialog
    :categories="categories"
    v-model:visible="selectCategoryDialogVisible"
    v-model:included="includedCategoryIds"
    v-model:excluded="excludedCategoryIds"
  />
</template>
